import { useEffect, useState } from "react";
import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import { MessageCircle, Menu, X, Globe, Home, Check, Car, BadgeCheck, MapPin } from "lucide-react";
import { useLang } from "@/lib/i18n";
import { useConfig } from "@/lib/auth";
import { trackVisitOnce } from "@/lib/track";
import { useSite, siteText } from "@/lib/site";
import { Logo } from "./ui";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";

const LANGS = [["he", "עברית"], ["ar", "العربية"]];

export const LangMenu = () => {
  const { t, lang, setLang } = useLang();
  const dir = lang === "he" || lang === "ar" ? "rtl" : "ltr";
  return (
    <DropdownMenu modal={false} dir={dir}>
      <DropdownMenuTrigger asChild>
        <button className="h-10 w-10 rounded-full grid place-items-center border border-white/15 text-zinc-300 hover:text-white hover:border-white/40 transition-colors" aria-label={t("nav.language")} data-testid="header-lang-button">
          <Globe size={18} />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="bg-black border-white/10 text-white min-w-[9rem]" data-testid="header-lang-menu">
        {LANGS.map(([code, label]) => (
          <DropdownMenuItem key={code} onSelect={() => setLang(code)} className="justify-between gap-4 py-2.5 cursor-pointer" data-testid={`header-lang-option-${code}`}>
            <span lang={code}>{label}</span>{lang === code && <Check size={14} className="text-[var(--xd-red)]" />}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

const NavLinks = ({ onClick, mobile }) => {
  const { t } = useLang();
  const cls = ({ isActive }) => `${mobile ? "py-3.5 text-base" : "py-2 text-sm"} tracking-wide transition-colors flex items-center gap-3 ${isActive ? "text-white" : "text-zinc-400 hover:text-white"}`;
  return (
    <>
      {mobile && <NavLink to="/" end className={cls} onClick={onClick} data-testid="nav-home"><Home size={16} />{t("nav.home")}</NavLink>}
      <NavLink to="/inventory" className={cls} onClick={onClick} data-testid="nav-inventory">{mobile && <Car size={16} />}{t("nav.inventory")}</NavLink>
      <NavLink to="/sold" className={cls} onClick={onClick} data-testid="nav-sold">{mobile && <BadgeCheck size={16} />}{t("nav.sold")}</NavLink>
    </>
  );
};

const Header = () => {
  const [open, setOpen] = useState(false);
  useEffect(() => {
    if (!open) { return; }
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);
  return (
    <header className="fixed top-0 inset-x-0 z-40 bg-black/80 backdrop-blur-md border-b border-white/5">
      <div className="mx-auto max-w-7xl flex items-center justify-between px-4 md:px-8 h-16 md:h-20">
        <Link to="/" data-testid="header-home-link"><Logo className="h-12 md:h-[4.5rem]" testid="header-logo" /></Link>
        <nav className="hidden md:flex items-center gap-8"><NavLinks /></nav>
        <div className="flex items-center gap-2">
          <LangMenu />
          <button className="md:hidden h-11 w-11 grid place-items-center" onClick={() => setOpen(!open)} aria-label="menu" aria-expanded={open} data-testid="mobile-menu-button">
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>
      {open && (
        <div className="md:hidden border-t border-white/5 bg-black px-6 py-4 flex flex-col divide-y divide-white/5" data-testid="mobile-menu">
          <NavLinks mobile onClick={() => setOpen(false)} />
        </div>
      )}
    </header>
  );
};

const fmtPhoneIntl = (n) => (/^972\d{9}$/.test(n) ? `+972-${n.slice(3, 5)}-${n.slice(5, 8)}-${n.slice(8)}` : `+${n}`);

const Footer = () => {
  const { t, lang } = useLang();
  const site = useSite();
  const { whatsapp } = useConfig();
  const link = "text-sm text-zinc-500 hover:text-white transition-colors";
  return (
    <footer className="border-t border-white/5 bg-black mt-24">
      <div className="xd-line" />
      <div className="mx-auto max-w-7xl px-6 md:px-8 py-14 grid gap-10 md:grid-cols-4">
        <div className="md:col-span-2 space-y-4">
          <Logo className="h-16" testid="footer-logo" />
          <p className="text-sm text-zinc-500 max-w-sm">{siteText(site, "footer_tagline", lang, t("footer.tagline"))}</p>
          {whatsapp && (
            <a href={`https://wa.me/${whatsapp}`} target="_blank" rel="noreferrer" className={link} data-testid="footer-whatsapp">
              <MessageCircle size={14} className="inline me-2" /><span className="ltr-num">{fmtPhoneIntl(whatsapp)}</span>
            </a>
          )}
          <p className="text-sm text-zinc-500 flex items-center" data-testid="footer-location"><MapPin size={14} className="inline me-2" />{t("footer.location")}</p>
        </div>
        <div className="space-y-3 flex flex-col">
          <p className="eyebrow">{t("footer.info")}</p>
          <Link className={link} to="/terms" data-testid="footer-terms">{t("page.terms")}</Link>
          <Link className={link} to="/privacy" data-testid="footer-privacy">{t("page.privacy")}</Link>
          <Link className={link} to="/about" data-testid="footer-about">{t("page.about")}</Link>
        </div>
        <div className="space-y-3 flex flex-col" data-testid="footer-account-access">
          <p className="eyebrow">{t("footer.access")}</p>
          <Link className={link} to="/login?as=broker" data-testid="footer-broker-login">{t("footer.broker")}</Link>
          <Link className={link} to="/login?as=dealer" data-testid="footer-dealer-login">{t("footer.dealer")}</Link>
          <Link className={link} to="/login?as=admin" data-testid="footer-admin-login">{t("footer.admin")}</Link>
        </div>
      </div>
      <p className="text-center text-xs text-zinc-700 pb-8 ltr-num w-full">© {new Date().getFullYear()} XD · myxd.it.com</p>
    </footer>
  );
};

export default function PublicLayout() {
  const loc = useLocation();
  useEffect(() => { trackVisitOnce(); }, []);
  useEffect(() => { window.scrollTo(0, 0); }, [loc.pathname]);
  return (
    <div className="min-h-screen bg-black text-white">
      <Header />
      <main className="pt-16 md:pt-20"><Outlet /></main>
      <Footer />
    </div>
  );
}
